import React, { useEffect, useState, useRef } from 'react';
import { firestore } from '../firebase';
import AddDevice from './AddDevice';
import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon, EyeIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/solid'
import PreviewModal from './PreviewModal';
import PermissionGate from './PermissionGate';
function Devices() {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(null); // Holds the device to edit
  const [loading, setLoading] = useState(true); // Loading state
  const [searchQuery, setSearchQuery] = useState(''); // Search query
  const [debouncedTerm, setDebouncedTerm] = useState(searchQuery);
  const [currentItem, setCurrentItem] = useState(null); // Holds the item to edit
  const [showPreview, setShowPreview] = useState(false); // Controls slideshow modal
  const [screenPositionFilter, setScreenPositionFilter] = useState(''); // Screen position filter
  const [storeCodeFilter, setStoreCodeFilter] = useState([]); // Store code filter (array for multi-select)
  const [storeCodeDropdownOpen, setStoreCodeDropdownOpen] = useState(false); // Controls dropdown visibility
  const [storeCodeSearchQuery, setStoreCodeSearchQuery] = useState(''); // Search query for store codes
  const dropdownRef = useRef(null); // Ref for click outside detection

  // useEffect to handle the debouncing logic
  useEffect(() => {
    // Set a timeout to update the debouncedTerm after 500ms
    const handler = setTimeout(() => {
      setDebouncedTerm(searchQuery);
    }, 300); // Adjust the delay here (500ms in this case)

    // Cleanup function to clear the timeout if the user types again before the delay
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStoreCodeDropdownOpen(false);
        setStoreCodeSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect to perform the action when filters change
  useEffect(() => {
    let temp = devices;

    // Apply search filter (Branch Name and Menu Item)
    if (debouncedTerm) {
      temp = temp.filter(
        (device) => {
          const deviceNameMatch = device.deviceName.toLowerCase().includes(debouncedTerm.toLowerCase());
          const menuItem = menuItems.find(item => item.id === device.menuItemId);
          const menuItemMatch = menuItem?.title.toLowerCase().includes(debouncedTerm.toLowerCase());
          return deviceNameMatch || menuItemMatch;
        }
      );
    }

    // Apply screen position filter
    if (screenPositionFilter) {
      temp = temp.filter((device) => device.screenPosition === screenPositionFilter);
    }

    // Apply store code filter (array) - match by deviceName (storeCode) combination
    if (storeCodeFilter.length > 0) {
      temp = temp.filter((device) => {
        const displayText = `${device.deviceName} (${device.storeCode})`;
        return storeCodeFilter.includes(displayText);
      });
    }

    setFilteredDevices(temp);
    // eslint-disable-next-line
  }, [debouncedTerm, screenPositionFilter, storeCodeFilter, devices]);

  useEffect(() => {
    const unsubscribeDevices = firestore.collection('cafeTVs').onSnapshot(snapshot => {
      const deviceList = snapshot.docs.map(doc => {
        const data = doc.data();
        return { id: doc.id, ...data, showAQI: data.showAQI ?? false };
      });
      setDevices(deviceList);
      setFilteredDevices(deviceList);
      setLoading(false); // Set loading to false once data is fetched
    });

    const unsubscribeMenuItems = firestore.collection('menuItems').onSnapshot(snapshot => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMenuItems(items);
    });

    return () => {
      unsubscribeDevices();
      unsubscribeMenuItems();
    };
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      await firestore.collection('cafeTVs').doc(id).delete();
    }
  };

  const handleEdit = (device) => {
    setCurrentDevice(device); // Set current device for editing
    setShowAdd(true);
  };

  const handlePreview = async(menuItemId) => {
    firestore.collection('menuItems').doc(menuItemId).onSnapshot(snapshot => {
          if (snapshot.exists) {
            const menuItem = { id: snapshot.id, ...snapshot.data() }; // Get data from snapshot
            setCurrentItem(menuItem); // Set the menu item state
            setShowPreview(true); // Show the preview modal
          } else {
            console.warn("Menu item not found");
          }
    });
  };

  // Get unique store code + device name combinations for filter dropdown
  const uniqueStoreDevices = devices
    .filter(device => device.storeCode)
    .map(device => ({
      storeCode: device.storeCode,
      deviceName: device.deviceName,
      displayText: `${device.deviceName} (${device.storeCode})`
    }))
    .filter((item, index, self) =>
      index === self.findIndex((t) => t.displayText === item.displayText)
    )
    .sort((a, b) => a.displayText.localeCompare(b.displayText));

  // Filter store devices based on search query
  const filteredStoreDevices = uniqueStoreDevices.filter(item =>
    item.displayText.toLowerCase().includes(storeCodeSearchQuery.toLowerCase())
  );

  // Toggle store code selection
  const toggleStoreCode = (code) => {
    if (storeCodeFilter.includes(code)) {
      setStoreCodeFilter(storeCodeFilter.filter(c => c !== code));
    } else {
      setStoreCodeFilter([...storeCodeFilter, code]);
    }
  };

  // Remove a specific store code from filter
  const removeStoreCode = (code) => {
    setStoreCodeFilter(storeCodeFilter.filter(c => c !== code));
  };


  return (
    <div className="m-auto p-10 max-w-screen-xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Devices</h2>
        <PermissionGate permission="tv.devices.create"><button
          onClick={() => {
            setCurrentDevice(null); // Clear current device to add a new one
            setShowAdd(true);
          }}
          className="bg-teal-500 text-white px-4 py-2 rounded"
        >
          Add Device
        </button></PermissionGate>
      </div>
      
      {/* Search Bar */}
      <div className="flex items-center border-2 border-grey-500 rounded-lg px-3 py-2 w-full mb-4">
      <MagnifyingGlassIcon className="h-5 w-5 text-black font-bold" />
      <input
        className="ml-2 w-full outline-none bg-transparent"
        type="text"
        placeholder="Search by Branch Name or Menu Item"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div className="flex-1">
          <label className="block mb-1 text-sm font-medium">Screen Position</label>
          <select
            className="w-full border-2 border-grey-500 rounded-lg px-3 py-2"
            value={screenPositionFilter}
            onChange={(e) => setScreenPositionFilter(e.target.value)}
          >
            <option value="">All Screen Positions</option>
            <option value="Screen 1">Screen 1</option>
            <option value="Screen 2">Screen 2</option>
            <option value="Screen 3">Screen 3</option>
          </select>
        </div>

        <div className="flex-1" ref={dropdownRef}>
          <label className="block mb-1 text-sm font-medium">Branch Name</label>
          <div className="relative">
            {/* Multi-select display */}
            <div
              className="w-full border-2 border-grey-500 rounded-lg px-3 py-2 cursor-pointer bg-white min-h-[42px] flex items-center justify-between"
              onClick={() => setStoreCodeDropdownOpen(!storeCodeDropdownOpen)}
            >
              <div className="flex flex-wrap gap-1 flex-1">
                {storeCodeFilter.length === 0 ? (
                  <span className="text-gray-400">All Branch Names</span>
                ) : (
                  storeCodeFilter.map(code => (
                    <span
                      key={code}
                      className="bg-teal-500 text-white px-2 py-1 rounded text-sm flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeStoreCode(code);
                      }}
                    >
                      {code}
                      <XMarkIcon className="h-3 w-3 cursor-pointer hover:text-gray-200" />
                    </span>
                  ))
                )}
              </div>
              <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${storeCodeDropdownOpen ? 'rotate-180' : ''}`} />
            </div>

            {/* Dropdown menu */}
            {storeCodeDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-grey-500 rounded-lg shadow-lg max-h-60 overflow-hidden">
                {/* Search input */}
                <div className="p-2 border-b">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded outline-none focus:border-teal-500"
                    placeholder="Search branch names..."
                    value={storeCodeSearchQuery}
                    onChange={(e) => setStoreCodeSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Options list */}
                <div className="max-h-44 overflow-y-auto">
                  {filteredStoreDevices.length === 0 ? (
                    <div className="px-3 py-2 text-gray-400 text-sm">No branch names found</div>
                  ) : (
                    filteredStoreDevices.map(item => (
                      <div
                        key={item.displayText}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                          storeCodeFilter.includes(item.displayText) ? 'bg-teal-50' : ''
                        }`}
                        onClick={() => toggleStoreCode(item.displayText)}
                      >
                        <input
                          type="checkbox"
                          checked={storeCodeFilter.includes(item.displayText)}
                          onChange={() => {}}
                          className="h-4 w-4"
                        />
                        <span>{item.displayText}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {(screenPositionFilter || storeCodeFilter.length > 0) && (
          <div className="flex items-end">
            <button
              onClick={() => {
                setScreenPositionFilter('');
                setStoreCodeFilter([]);
              }}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
      {showAdd && (
        <AddDevice
          onClose={() => setShowAdd(false)}
          menuItems={menuItems}
          currentDevice={currentDevice} // Pass current device if in edit mode
        />
      )}

      {/* Loader */}
      {loading ? (
        <div className="text-center py-4">
          <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
          <p className='mt-2'>Loading devices...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white shadow rounded text-left">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Cafe Code</th>
                <th className="py-2 px-4 border-b">Branch Name</th>
                <th className="py-2 px-4 border-b">Screen Position</th>
                <th className="py-2 px-4 border-b">Menu Item</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map((device) => (
                <tr key={device.id}>
                  <td className="py-2 px-4 border-b">{device.storeCode || ""}</td>
                  <td className="py-2 px-4 border-b">{device.deviceName}</td>
                  <td className="py-2 px-4 border-b">{device.screenPosition || ""}</td>
                  <td className="py-2 px-4 border-b">
                    {menuItems.find((item) => item.id === device.menuItemId)?.title || 'Unassigned'}
                  </td>
                  <td className="py-2 px-4 border-b">
                  <button
                        onClick={() => handlePreview(device.menuItemId)}
                        className="text-teal-500 mr-2 hover:opacity-70"
                      >
                        <EyeIcon className='size-8'/>
                      </button>
                    <PermissionGate permission="tv.devices.update"><button
                      onClick={() => handleEdit(device)}
                      className="text-teal-500 mr-2 hover:opacity-70"
                    >
                      <PencilSquareIcon className="size-8" />
                    </button></PermissionGate>
                    <PermissionGate permission="tv.devices.delete"><button
                      onClick={() => handleDelete(device.id)}
                      className="text-red-500 hover:opacity-70"
                    >
                      <TrashIcon className="size-8" />
                    </button></PermissionGate>
                  </td>
                </tr>
              ))}
              {filteredDevices.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No devices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Preview modal */}
      <PreviewModal
        currentItem={currentItem}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
      />
    </div>
  );
}

export default Devices;
